import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RecordPeek, PeekLink, type PeekTarget } from '../components/RecordPeek';
import { api } from '../lib/api';
import { can, useAuth, ROLE } from '../lib/auth';
import { formatJc, fetchAllQueue, num, sheetsFor, money } from './Dashboard';
import { ProgressCell, type PoProgress } from './FlowQueue';
import { ClientSummary } from '../components/ClientSummary';

/**
 * Orders: every PO in the order flow (since 13 Aug 2026) that is not
 * dispatched yet - its type, job card, DOD, board and design - and where it
 * is on the floor, design to dispatch.  It used to be a panel on the
 * dashboard.  VIEW_OVERSIGHT.
 */

interface PendingJobRow {
  poId: string;
  poNumber: string | null;
  client: string | null;
  productName: string | null;
  jobName: string | null;
  orderType: string | null;
  qty: string | null;
  jcQty: number | null;
  promiseDate: string | null;
  boardStatus: string | null;
  boardExpectedDate: string | null;
  clientId: string | null;
  productId: string | null;
  poDate: string | null;
  crmName: string | null;
  rate: string | null;
  ups: string | null;
  /** Boxes already sent against this PO (all its job cards). */
  poDispatched?: number | null;
  jobId: string | null;
  linkedPoCount: number | null;
  designStatus?: string | null;
  designDoneByName?: string | null;
}

/** The order types the PO form sets; anything else is not confirmed yet. */
const ORDER_TYPES = ['New', 'Change', 'Repeat', 'Repeat With Change', 'Unconfirmed'] as const;
const orderTypeOf = (t: string | null): string => ((ORDER_TYPES as readonly string[]).includes(t ?? '') ? t! : 'Unconfirmed');
const TYPE_TONE: Record<string, string> = {
  New: 'bg-blue-50 text-blue-700', Change: 'bg-amber-50 text-amber-700', Repeat: 'bg-emerald-50 text-emerald-700',
  'Repeat With Change': 'bg-purple-50 text-purple-700', Unconfirmed: 'bg-slate-100 text-slate-500',
};
function OrderTypeBadge({ type }: { type: string | null }) {
  const t = orderTypeOf(type);
  return <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${TYPE_TONE[t]}`}>{t}</span>;
}


export function OrdersPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  /** Process Coordinator / VPPL: may look across every CRM and narrow to one. */
  const seesAllCrms = can(user, 'VIEW_ALL_CRMS');
  const [crmFilter, setCrmFilter] = useState('');
  const crmList = useQuery({
    enabled: seesAllCrms,
    queryKey: ['flow-crms'],
    queryFn: () => api<{ id: number; name: string }[]>('/api/flow/crms'),
    staleTime: 5 * 60_000,
  });
  const [peek, setPeek] = useState<PeekTarget | null>(null);

  const pendingJobs = useQuery({
    queryKey: ['orders-in-flow', crmFilter],
    // Every PO in the flow window, not just the ones still missing a job card.
    // The JC number can only "fill in as the job card is created" if the row
    // survives that moment — filtering on Job_ID IS NULL removed it instead.
    // The queue pages (200 a page at most) and this panel counts and filters
    // client-side, so it fetches every page - never only the first 200.
    queryFn: () => fetchAllQueue<PendingJobRow>({
      kind: 'all',
      // Honoured by the API only for callers holding VIEW_ALL_CRMS.
      ...(seesAllCrms && crmFilter ? { crmUserId: crmFilter } : {}),
    }),
  });

  const [pendingSearch, setPendingSearch] = useState('');
  // Two dropdown filters, same shape as the CRM dropdown below. Replaces
  // the "Awaiting job card only" / "Awaiting DOD only" checkboxes that used
  // to sit here — dropdowns keep the filter row compact and add board
  // filtering in the same visual pattern the operator already knows.
  const [stageFilter, setStageFilter] = useState<'all' | 'awaiting_jc' | 'has_jc' | 'awaiting_dod'>('all');
  const [boardFilter, setBoardFilter] = useState<'all' | 'no_status' | 'expected' | 'available'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  // Toggle: when on, show the LAST job card's date + machine + colors
  // under every REPEAT row.  Off by default because it costs an extra
  // POST with every visible product ID.
  const [showRepeatHistory, setShowRepeatHistory] = useState(false);
  const allPending = pendingJobs.data?.rows ?? [];

  // Batch-fetch last-job details for every REPEAT product ID on screen.
  const repeatProductIds = allPending
    .filter((r) => r.orderType === 'Repeat' && r.productId)
    .map((r) => r.productId!)
    .filter((v, i, a) => a.indexOf(v) === i);
  const repeatHistory = useQuery({
    enabled: showRepeatHistory && repeatProductIds.length > 0,
    // Cache-key over the sorted id set so the result survives search filters
    // that only whittle down the on-screen rows.
    queryKey: ['orders-repeat-history', [...repeatProductIds].sort().join(',')],
    queryFn: () => api<Record<string, {
      jobId: string;
      jobCardDate: string | null;
      machineName: string | null;
      colors: string | null;
    }>>(`/api/job-orders/last-by-product`, {
      method: 'POST',
      body: JSON.stringify({ productIds: repeatProductIds }),
    }),
    staleTime: 60_000,
  });

  // The tiles and the header badge measure OUTSTANDING WORK, so they count POs
  // with no job card — never the whole table. Widening the table to expose JC
  // numbers must not quietly change what those four numbers mean.
  const needsJobCard = allPending.filter((r) => !r.jobId);
  const needsDod = allPending.filter((r) => !r.promiseDate);

  // Filtered client-side: the working set is already loaded, so this is instant
  // and needs no extra round trip.
  const pendingQ = pendingSearch.trim().toLowerCase();
  let scoped = allPending as PendingJobRow[];
  if (typeFilter !== 'all') scoped = scoped.filter((r) => orderTypeOf(r.orderType) === typeFilter);
  if (boardFilter === 'no_status') {
    scoped = scoped.filter((r) => !r.boardStatus || r.boardStatus === 'Not Ready');
  } else if (boardFilter === 'expected') {
    // The API stamps this exact string when a board has been ordered but
    // hasn't arrived; boardExpectedDate carries the promised date.
    scoped = scoped.filter((r) => r.boardStatus === 'Expected');
  } else if (boardFilter === 'available') {
    scoped = scoped.filter((r) => r.boardStatus === 'Available');
  }
  // Repeat-history toggle also narrows to Repeat rows only, since the
  // extra Machine + Colors columns only make sense for those rows.
  if (showRepeatHistory) scoped = scoped.filter((r) => r.orderType === 'Repeat');
  // Every filter but the stage tab: the By client view splits these by stage.
  const beforeStage = pendingQ
    ? scoped.filter((r) => [r.poNumber, r.client, r.productName, r.jobName, r.orderType, r.jobId, r.designDoneByName, r.designStatus]
        .some((f) => (f ?? '').toLowerCase().includes(pendingQ)))
    : scoped;
  const pending = stageFilter === 'awaiting_jc' ? beforeStage.filter((r) => !r.jobId)
    : stageFilter === 'has_jc' ? beforeStage.filter((r) => !!r.jobId)
    : stageFilter === 'awaiting_dod' ? beforeStage.filter((r) => !r.promiseDate)
    : beforeStage;
  const [byClient, setByClient] = useState(false);

  // Totals reflect what is on screen, so they agree with the rows below -
  // and only what is still to send: a part-dispatched PO counts its balance.
  const balanceOf = (r: PendingJobRow) => Math.max(0, num(r.qty) - Math.round(Number(r.poDispatched) || 0));
  const totalValue  = pending.reduce((a, r) => a + balanceOf(r) * num(r.rate), 0);
  const totalSheets = pending.reduce((a, r) => a + sheetsFor({ qty: String(balanceOf(r)), ups: r.ups }), 0);
  const totalQty    = pending.reduce((a, r) => a + balanceOf(r), 0);
  const totalSent   = pending.reduce((a, r) => a + Math.round(Number(r.poDispatched) || 0), 0);
  const [repeatError, setRepeatError] = useState<string | null>(null);
  const isSuperAdmin = user?.rollId === ROLE.SuperAdmin;
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingJc, setCancellingJc] = useState<string | null>(null);

  const closeJob = useMutation({
    mutationFn: (v: { jobId: string; reason: string }) =>
      api('/api/flow/close-job', { method: 'POST', body: JSON.stringify(v) }),
    onSuccess: () => {
      setCancellingJc(null); setCancelReason('');
      qc.invalidateQueries({ queryKey: ['orders-in-flow'] });
      qc.invalidateQueries({ queryKey: ['flow-queue'] });
    },
    onError: (e) => setRepeatError((e as Error).message),
  });


  // Where each PO is on the floor - 100 at a time, as the API takes them.
  const allIds = allPending.map((r) => r.poId);
  const progress = useQuery({
    enabled: allIds.length > 0,
    queryKey: ['orders-progress', allIds.join(',')],
    queryFn: async () => {
      const chunks: string[][] = [];
      for (let i = 0; i < allIds.length; i += 100) chunks.push(allIds.slice(i, i + 100));
      const parts = await Promise.all(chunks.map((c) =>
        api<{ rows: PoProgress[] }>(`/api/flow/po-progress?pos=${encodeURIComponent(c.join(','))}`)));
      return parts.flatMap((x) => x.rows);
    },
    staleTime: 60_000,
  });
  const progressOf = new Map((progress.data ?? []).map((x) => [x.poId, x]));

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div>
        <h1 className="text-xl font-semibold text-brand">Orders</h1>
        <p className="text-sm text-slate-500">Active orders: every PO since 13 Aug 2026 not yet dispatched in full, and where it is now. The JC number fills in once its job card is raised.</p>
      </div>
      {pendingJobs.isLoading && <div className="card p-8 text-center text-slate-500">Loading purchase orders…</div>}
      {pendingJobs.isError && <div className="card p-8 text-center text-red-600">Could not load: {(pendingJobs.error as Error).message}</div>}
      {pendingJobs.data && (() => {
        const stageTab = (key: typeof stageFilter, label: string, n: number, tone: string) => (
          <button key={key} onClick={() => setStageFilter(key)}
                  className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium ${stageFilter === key ? 'border-brand text-brand' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800'}`}>
            {label}
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${stageFilter === key ? 'bg-brand text-white' : tone}`}>{n.toLocaleString('en-IN')}</span>
          </button>
        );
        const typeCounts = ORDER_TYPES.map((t) => [t, allPending.filter((r) => orderTypeOf(r.orderType) === t).length] as const);
        const filtered = !!(pendingQ || typeFilter !== 'all' || boardFilter !== 'all' || stageFilter !== 'all' || showRepeatHistory);
        return (
        <div className="card overflow-hidden">
          <div className="flex flex-wrap items-end gap-x-1 overflow-x-auto border-b border-slate-200 bg-white px-2">
            {stageTab('all', 'All', allPending.length, 'bg-slate-100 text-slate-600')}
            {stageTab('awaiting_jc', 'Awaiting job card', needsJobCard.length, 'bg-brand-50 text-brand')}
            {stageTab('has_jc', 'Job card raised', allPending.length - needsJobCard.length, 'bg-emerald-50 text-emerald-700')}
            {stageTab('awaiting_dod', 'Awaiting DOD', needsDod.length, 'bg-amber-50 text-amber-700')}
            <label className="ml-auto flex cursor-pointer items-center gap-1.5 self-center whitespace-nowrap rounded-md border border-slate-200 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
                   title="How many orders each client has, how many have a job card and how many are waiting for one">
              <input type="checkbox" className="h-3.5 w-3.5" checked={byClient} onChange={(e) => setByClient(e.target.checked)} />
              By client
            </label>
          </div>
          {repeatError && (
            <div className="border-b border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{repeatError}</div>
          )}

          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-white px-3 py-2.5">
            <div className="relative w-full sm:w-72">
              <input className="input py-1.5 pl-8" placeholder="Search PO, JC, client, product…"
                     value={pendingSearch} onChange={(e) => setPendingSearch(e.target.value)} />
              <svg className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 3.3 9.9l3.15 3.15a.75.75 0 1 0 1.06-1.06l-3.15-3.15A5.5 5.5 0 0 0 9 3.5ZM5 9a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z" clipRule="evenodd" /></svg>
            </div>
            <select className="input !w-auto py-1.5" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} title="Order type">
              <option value="all">All types ({allPending.length})</option>
              {typeCounts.filter(([, n]) => n > 0).map(([t, n]) => <option key={t} value={t}>{t} ({n})</option>)}
            </select>
            <select className="input !w-auto py-1.5" value={boardFilter} onChange={(e) => setBoardFilter(e.target.value as typeof boardFilter)} title="Board">
              <option value="all">All boards</option>
              <option value="no_status">Board: No status</option>
              <option value="expected">Board: Expected</option>
              <option value="available">Board: Available</option>
            </select>
            {/* CRM is filtered on the server: the panel then loads that CRM's whole list. */}
            {seesAllCrms && (
              <select className="input !w-auto py-1.5" value={crmFilter} onChange={(e) => setCrmFilter(e.target.value)} title="CRM">
                <option value="">All CRMs</option>
                {crmList.data?.map((c) => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
                <option value="unassigned">— Unassigned —</option>
              </select>
            )}
            {/* The last job card's date, machine and colours under each Repeat; off by default, it costs a lookup. */}
            <label className="flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50">
              <input type="checkbox" className="h-3.5 w-3.5" checked={showRepeatHistory} onChange={(e) => setShowRepeatHistory(e.target.checked)} />
              Repeat history
            </label>
            {filtered && (
              <button className="text-sm text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline"
                      onClick={() => { setPendingSearch(''); setTypeFilter('all'); setBoardFilter('all'); setStageFilter('all'); setShowRepeatHistory(false); }}>
                Clear filters
              </button>
            )}
          </div>
          {/* Totals follow the filters, so they always describe the rows shown. */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-b border-slate-100 bg-slate-50 px-4 py-2 text-xs">
            <span className="text-slate-500">
              <span className="font-semibold text-slate-800">{pending.length.toLocaleString('en-IN')}</span> of {allPending.length.toLocaleString('en-IN')} POs
            </span>
            {totalSent > 0 && <span className="ml-auto text-slate-500" title="Boxes already dispatched against these POs - taken off the totals">Sent <span className="font-semibold text-emerald-700">{money(totalSent)}</span></span>}
            <span className={`${totalSent > 0 ? '' : 'ml-auto '}text-slate-500`} title="Still to send: PO quantity less what has been dispatched">Boxes left <span className="font-semibold text-slate-800">{money(totalQty)}</span></span>
            <span className="text-slate-500" title="For the boxes left">Sheets <span className="font-semibold text-slate-800">{money(totalSheets)}</span></span>
            <span className="text-slate-500" title="The boxes left at each PO's rate">Value left <span className="font-semibold text-brand">₹{money(totalValue)}</span></span>
          </div>
          {byClient ? (
            <ClientSummary rows={beforeStage} balanceOf={balanceOf}
                           onOpenPo={(id) => setPeek({ kind: 'po', id })} />
          ) : (
          /* Six columns, details stacked inside each - no sideways scrolling. */
          <div className="max-h-[72vh] overflow-y-auto font-sans">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-[5] bg-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-500 shadow-[0_1px_0_#e2e8f0]">
                <tr>
                  <th className="px-4 py-2">Order</th>
                  <th className="px-3 py-2">JC</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2">DOD · Board</th>
                  <th className="px-3 py-2">Where it is</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((r) => {
                  const h = showRepeatHistory && r.productId ? repeatHistory.data?.[r.productId] : null;
                  const d = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '');
                  return (
                    <tr key={r.poId} className="group border-t border-slate-100 align-top hover:bg-slate-50">
                      {/* Order: what, for whom, which PO */}
                      <td className="min-w-[16rem] px-4 py-2.5">
                        <div className="font-medium leading-snug text-slate-800">
                          {r.productId
                            ? <PeekLink className="hover:text-brand hover:underline" title="Open the product specification"
                                        onOpen={() => setPeek({ kind: 'product', id: r.productId! })}>
                                {r.productName ?? r.jobName}
                              </PeekLink>
                            : r.jobName}
                        </div>
                        <div className="mt-0.5 text-xs text-slate-500">
                          {r.clientId
                            ? <PeekLink className="hover:underline" title="View this client" onOpen={() => setPeek({ kind: 'client', id: r.clientId! })}>{r.client}</PeekLink>
                            : r.client}
                          {seesAllCrms && <span className="text-slate-400"> · {r.crmName ?? <span className="text-amber-700">no CRM</span>}</span>}
                        </div>
                        <div className="mt-0.5 font-mono text-[11px] text-slate-400">
                          <PeekLink className="hover:text-brand hover:underline" title="View this purchase order" onOpen={() => setPeek({ kind: 'po', id: r.poId })}>
                            {r.poNumber || r.poId}
                          </PeekLink>
                        </div>
                        {showRepeatHistory && (
                          <div className="mt-1 text-[11px] text-slate-500">
                            {h ? <>Last JC <span className="font-medium text-slate-700">{formatJc(h.jobId)}</span>{h.jobCardDate && ` · ${d(h.jobCardDate)}`}{h.machineName && ` · ${h.machineName}`}{h.colors && ` · ${h.colors}`}</>
                              : repeatHistory.isLoading ? '…' : 'No earlier job card'}
                          </div>
                        )}
                      </td>
                      {/* Job card */}
                      <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs">
                        {r.jobId
                          ? <>
                              <PeekLink className="font-semibold text-brand hover:underline" title="Open the job card"
                                        onOpen={() => setPeek({ kind: 'job', id: String(r.jobId) })}>
                                {formatJc(r.jobId)}
                              </PeekLink>
                              {(r.linkedPoCount ?? 0) > 1 && (
                                <span className="ml-1 rounded bg-blue-100 px-1 font-sans text-[10px] font-semibold text-blue-700" title={`Clubbed: ${r.linkedPoCount} POs on this job card`}>
                                  {r.linkedPoCount} PO
                                </span>
                              )}
                              {isSuperAdmin && cancellingJc !== String(r.jobId) && (
                                <div><button className="mt-1 font-sans text-[11px] text-red-600 opacity-0 hover:underline focus:opacity-100 group-hover:opacity-100"
                                             onClick={() => { setCancellingJc(String(r.jobId)); setCancelReason(''); }}>Cancel JC</button></div>
                              )}
                              {isSuperAdmin && cancellingJc === String(r.jobId) && (
                                <div className="mt-1 flex flex-col gap-1 font-sans">
                                  <input className="input w-40 py-0.5 text-xs" placeholder="Reason…" autoFocus
                                         value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                                  <div className="flex gap-2">
                                    <button className="rounded bg-red-600 px-2 py-0.5 text-[11px] text-white hover:bg-red-700 disabled:opacity-50"
                                            disabled={!cancelReason.trim() || closeJob.isPending}
                                            onClick={() => { if (confirm(`Close job card ${r.jobId}? It will be removed from all queues.`)) closeJob.mutate({ jobId: String(r.jobId), reason: cancelReason.trim() }); }}>
                                      {closeJob.isPending ? '…' : 'Close it'}
                                    </button>
                                    <button className="text-[11px] text-slate-500 hover:text-slate-700" onClick={() => setCancellingJc(null)}>Keep</button>
                                  </div>
                                </div>
                              )}
                            </>
                          : <span className="font-sans text-[11px] text-amber-700">not raised</span>}
                      </td>
                      <td className="px-3 py-2.5"><OrderTypeBadge type={r.orderType} /></td>
                      {/* Qty: PO, and the job card's when it differs */}
                      <td className="whitespace-nowrap px-3 py-2.5 text-right text-xs">
                        <div className="font-semibold text-slate-800">{num(r.qty) ? num(r.qty).toLocaleString('en-IN') : r.qty}</div>
                        {r.jcQty != null && r.jcQty > 0 && r.jcQty !== num(r.qty) && (
                          <div className="text-blue-700" title="The job card's quantity">JC {r.jcQty.toLocaleString('en-IN')}</div>
                        )}
                        {Math.round(Number(r.poDispatched) || 0) > 0 && (
                          <div className="text-emerald-700" title="Boxes dispatched against this PO">
                            sent {Math.round(Number(r.poDispatched)).toLocaleString('en-IN')} · left {balanceOf(r).toLocaleString('en-IN')}
                          </div>
                        )}
                        <div className="text-[11px] text-slate-400">{d(r.poDate)}</div>
                      </td>
                      {/* DOD and board */}
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                        {r.promiseDate ? <div className="font-medium text-slate-800">{d(r.promiseDate)}</div> : <div className="font-medium text-red-600">No DOD</div>}
                        <div className={`text-[11px] ${r.boardStatus === 'Available' ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {r.boardStatus === 'Available' ? 'Board ready'
                            : (!r.boardStatus || r.boardStatus === 'Not Ready') ? 'Board: no status'
                            : <>Board {r.boardStatus.toLowerCase()}{r.boardExpectedDate && ` ${d(r.boardExpectedDate)}`}</>}
                        </div>
                      </td>
                      <td className="min-w-[13rem] px-3 py-2.5">
                        <ProgressCell p={progressOf.get(r.poId)} name={r.poNumber || r.poId} loading={progress.isLoading} />
                      </td>
                    </tr>
                  );
                })}
                {pending.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-500">
                      {filtered ? 'No purchase orders match these filters.' : 'No purchase orders in the flow window yet.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </div>
        );
      })()}

      {peek && <RecordPeek target={peek} onClose={() => setPeek(null)} />}
    </div>
  );
}

